import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, of } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { NotificationService } from '../../../services/notification.service';
import {
  PlanChangeLocation,
  PlanChangeSettings,
  PlanChangeSettingsData,
  PlanChangeSettingsService,
  PlanChangeSettingsSource,
  PlanChangeSettingsUpdatePayload,
} from '../../../services/plan-change-settings.service';

const OFF: PlanChangeSettings = {
  enabled: false,
  location_ids: [],
  updated_by: null,
  updated_at: null,
};

/**
 * CWS-1426: the switch for membership plan changes. Saves on every toggle, the
 * way the vendor plate-matching settings do, and renders whatever the server
 * hands back. Hosted on the super-admin company page since CWS-1598; the
 * `source` input is how that page points it at the company-scoped API.
 */
@Component({
  selector: 'app-plan-change-settings',
  standalone: true,
  imports: [CommonModule],
  providers: [NotificationService],
  templateUrl: './plan-change-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./plan-change-settings.component.scss'],
})
export class PlanChangeSettingsComponent implements OnChanges, OnDestroy {
  private readonly settingsService = inject(PlanChangeSettingsService);
  private readonly messageService = inject(NotificationService);
  private readonly destroy$ = new Subject<void>();

  @Input() isActive = false;
  /** Where settings are read and written; defaults to the shop's own endpoints. */
  @Input() source: PlanChangeSettingsSource | null = null;
  /** Mirrors `isLoading` so the host can hold its reveal until this section has loaded. */
  @Output() loadingChange = new EventEmitter<boolean>();

  settings: PlanChangeSettings = OFF;
  locations: PlanChangeLocation[] = [];
  globallyEnabled = true;

  isLoading = false;
  isSaving = false;

  get isInteractionDisabled(): boolean {
    return this.isLoading || this.isSaving;
  }

  /** Empty allowlist means every location. */
  get isRestrictedToLocations(): boolean {
    return this.settings.location_ids.length > 0;
  }

  isLocationSelected(locationId: string): boolean {
    const wanted = String(locationId).toLowerCase();
    return this.settings.location_ids.some(id => id.toLowerCase() === wanted);
  }

  locationLabel(location: PlanChangeLocation): string {
    return location.branch || location.id;
  }

  trackLocation(_index: number, location: PlanChangeLocation): string {
    return location.id;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isActive']?.currentValue === true || (this.isActive && changes['source'])) {
      this.loadSettings();
    }
  }

  private get dataSource(): PlanChangeSettingsSource {
    return this.source ?? this.settingsService;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onToggleEnabled(): void {
    if (this.isInteractionDisabled) {
      return;
    }
    this.save({ enabled: !this.settings.enabled, location_ids: this.settings.location_ids });
  }

  /**
   * Unrestricted shows every location ticked, so the first untick names every
   * other location explicitly. Unticking the last remaining location switches the
   * feature off for the tenant (CWS-1607): an empty allowlist would mean "every
   * location", the opposite of what was asked, and silently refusing the click
   * left the admin with no way to express "nowhere" from this list.
   */
  onToggleLocation(locationId: string): void {
    if (this.isInteractionDisabled || !this.settings.enabled) {
      return;
    }
    const wanted = String(locationId).toLowerCase();
    const current = this.isRestrictedToLocations
      ? this.settings.location_ids.map(id => id.toLowerCase())
      : this.locations.map(l => String(l.id).toLowerCase());
    const next = current.includes(wanted)
      ? current.filter(id => id !== wanted)
      : [...current, wanted];
    if (next.length === 0) {
      this.save({ enabled: false, location_ids: [] });
      return;
    }
    const everyLocation =
      this.locations.length > 0 &&
      this.locations.every(l => next.includes(String(l.id).toLowerCase()));
    this.save({ enabled: true, location_ids: everyLocation ? [] : next });
  }

  onAllowEveryLocation(): void {
    if (this.isInteractionDisabled || !this.isRestrictedToLocations) {
      return;
    }
    this.save({ enabled: this.settings.enabled, location_ids: [] });
  }

  private applyData(data: PlanChangeSettingsData | null | undefined): void {
    this.settings = data?.settings ?? OFF;
    this.locations = data?.locations ?? [];
    this.globallyEnabled = data?.globally_enabled !== false;
  }

  private setLoading(loading: boolean): void {
    if (this.isLoading === loading) {
      return;
    }
    this.isLoading = loading;
    this.loadingChange.emit(loading);
  }

  private loadSettings(): void {
    this.setLoading(true);
    this.dataSource
      .getSettings()
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Load failed',
            detail: err.error?.message || 'Could not load membership plan change settings.',
          });
          return of(null);
        }),
        finalize(() => this.setLoading(false))
      )
      .subscribe(res => {
        if (res == null) {
          return;
        }
        if (res.status === false) {
          this.messageService.add({
            severity: 'error',
            summary: 'Load failed',
            detail: res.message || 'Could not load membership plan change settings.',
          });
          return;
        }
        this.applyData(res.data);
      });
  }

  private save(payload: PlanChangeSettingsUpdatePayload): void {
    this.isSaving = true;
    this.dataSource
      .updateSettings(payload)
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail: err.error?.message || 'Could not save membership plan change settings.',
          });
          return of(null);
        }),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe(res => {
        if (res == null) {
          return;
        }
        if (res.status === false) {
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail: res.message || 'The server did not save the settings.',
          });
          return;
        }
        this.applyData(res.data);
        this.messageService.add({
          severity: 'success',
          summary: 'Saved',
          detail: this.settings.enabled
            ? this.isRestrictedToLocations
              ? `Membership plan changes are on at ${this.settings.location_ids.length} location(s).`
              : 'Membership plan changes are on at every location.'
            : 'Membership plan changes are off.',
        });
      });
  }
}
